import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { HttpErrorToastService } from '@core/services/http-error-toast.service';
import { environment } from '@env';
import { Observable, retry, throwError, timer } from 'rxjs';
import { catchError } from 'rxjs/operators';

export const DISABLE_TOAST = new HttpContextToken<boolean>(() => false);
export const disableToast = () => new HttpContext().set(DISABLE_TOAST, true);

const INVALID_MARTECH_AAD_TOKEN_CODE = '401_MPM01_BE9002';
const UNDICI_SOCKET_ERROR_CODE = '502_MPM01_IEUND_ERR_SOCKET';

function extractErrorCode(error: HttpErrorResponse): string | null {
  const payload = error?.error;
  const topLevelCode = (error as HttpErrorResponse & { code?: string }).code;

  if (error?.error?.code === UNDICI_SOCKET_ERROR_CODE) {
    return error.error.code;
  }

  if (topLevelCode === UNDICI_SOCKET_ERROR_CODE) {
    return topLevelCode;
  }

  if (!payload) {
    return null;
  }

  if (typeof payload === 'object' && 'code' in payload && typeof payload.code === 'string') {
    return payload.code;
  }

  return null;
}

function shouldRetryUndiciSocketError(error: HttpErrorResponse): boolean {
  return extractErrorCode(error) === UNDICI_SOCKET_ERROR_CODE;
}

function shouldRetryLoginForMartech(error: HttpErrorResponse, req: HttpRequest<unknown>): boolean {
  const isMartechRequest = req.url.startsWith(environment.api_root_martech);
  const code = extractErrorCode(error);

  return isMartechRequest && error.status === 401 && code === INVALID_MARTECH_AAD_TOKEN_CODE;
}

/**
 * Show toast error and send to monitoring service
 * the error should have the following structure:
 *  {
 *    "resultMsg": "this the messages",
 *    "resultCode": "123",
 *    "resultDetails": []
 *  }
 */
export function HttpErrorInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const httpErrorToastService = inject(HttpErrorToastService);
  const msalService = inject(MsalService);

  return next(req).pipe(
    retry({
      count: 1,
      delay: (error: HttpErrorResponse) => (shouldRetryUndiciSocketError(error) ? timer(0) : throwError(() => error)),
    }),
    catchError((error: HttpErrorResponse) => {
      if (shouldRetryLoginForMartech(error, req)) {
        msalService.loginRedirect();
      }

      // Show toast error
      if (!req.context.get(DISABLE_TOAST)) httpErrorToastService.handle(error);

      // Send to monitoring service
      // monitoringService.httpError(error, req);

      return throwError(() => error);
    })
  );
}
